import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { employeeAttendanceAPI, employeeAPI } from '../../services/api';

const AttendanceRecords = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(20);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');

  // Fetch attendance records
  const { data: attendanceData, isLoading } = useQuery({
    queryKey: ['attendance-records', currentPage, startDate, endDate, selectedEmployee],
    queryFn: () =>
      employeeAttendanceAPI
        .getAttendanceRecords(
          currentPage * itemsPerPage,
          itemsPerPage,
          startDate || null,
          endDate || null,
          selectedEmployee || null
        )
        .then(res => res.data),
  });

  // Fetch employees for filter
  const { data: employeesData } = useQuery({
    queryKey: ['employees-list'],
    queryFn: () => employeeAPI.getAll(0, 1000, true).then(res => res.data),
  });

  const attendances = attendanceData?.attendances || [];
  const total = attendanceData?.total || 0;
  const employees = Array.isArray(employeesData) ? employeesData : (employeesData?.employees || []);

  const formatDate = (dateString) => {
    // Ensure the date string is treated as UTC by appending 'Z' if not present
    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    return new Date(utcString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'Asia/Kolkata'
    });
  };

  const formatTime = (dateString) => {
    // Ensure the date string is treated as UTC by appending 'Z' if not present
    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    return new Date(utcString).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    });
  };

  const calculateWorkHours = (checkIn, checkOut) => {
    if (!checkOut) return '-';
    const diff = new Date(checkOut) - new Date(checkIn);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedEmployee('');
    setCurrentPage(0);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to="/employee-attendance"
          className="flex items-center text-gray-300 hover:text-gray-100 mb-4 text-sm"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Attendance Management
        </Link>
        <h1 className="text-2xl sm:text-3xl font-bold text-white">Attendance Records</h1>
        <p className="text-gray-300 mt-1 sm:mt-2 text-sm sm:text-base">
          View and filter employee attendance records
        </p>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Employee
            </label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Employees</option>
              {employees.map((emp) => (
                <option key={emp.employee_id} value={emp.employee_id}>
                  {emp.first_name} {emp.last_name} ({emp.employee_id})
                </option>
              ))}
            </select>
          </div>
        </div>

        {(startDate || endDate || selectedEmployee) && (
          <div className="mt-4">
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-colors text-sm"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Records Table */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">
            {total} Record{total !== 1 ? 's' : ''}
          </h2>
        </div>

        {attendances.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <CalendarIcon className="mx-auto h-12 w-12 text-gray-500" />
            <h3 className="mt-2 text-sm font-medium text-white">No records found</h3>
            <p className="mt-1 text-sm text-gray-400">
              Try adjusting your filters or check back later
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Employee Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Check In
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Check Out
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Hours
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Work Summary
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {attendances.map((record) => (
                    <tr key={record._id || record.id} className="hover:bg-gray-700/50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {formatDate(record.attendance_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-white">{record.employee_name || record.employee_id}</div>
                        <div className="text-xs text-gray-400">{record.employee_id}</div>
                        <div className="text-xs text-gray-500">{record.check_in_phone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-400">
                        {formatTime(record.check_in_time)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {record.check_out_time ? (
                          <span className="text-red-400">{formatTime(record.check_out_time)}</span>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {calculateWorkHours(record.check_in_time, record.check_out_time)}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {record.work_before_lunch ? (
                          <div className="space-y-1">
                            <details className="cursor-pointer">
                              <summary className="text-blue-400 hover:text-blue-300">
                                View Work Summary
                              </summary>
                              <div className="mt-2 p-3 bg-gray-700 rounded text-xs space-y-2">
                                <div>
                                  <p className="text-gray-400 font-medium mb-1">Before Lunch:</p>
                                  <p className="text-gray-300">{record.work_before_lunch}</p>
                                </div>
                                <div>
                                  <p className="text-gray-400 font-medium mb-1">After Lunch:</p>
                                  <p className="text-gray-300">{record.work_after_lunch}</p>
                                </div>
                              </div>
                            </details>
                          </div>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-700">
              {attendances.map((record) => (
                <div key={record._id || record.id} className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="text-sm font-medium text-white">{record.employee_name || record.employee_id}</div>
                      <div className="text-xs text-gray-400">{record.employee_id}</div>
                      <div className="text-xs text-gray-400">{formatDate(record.attendance_date)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-white">
                        {calculateWorkHours(record.check_in_time, record.check_out_time)}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-400">Check In:</span>
                      <div className="text-green-400">{formatTime(record.check_in_time)}</div>
                    </div>
                    <div>
                      <span className="text-gray-400">Check Out:</span>
                      <div className="text-red-400">
                        {record.check_out_time ? formatTime(record.check_out_time) : '-'}
                      </div>
                    </div>
                  </div>
                  {record.work_before_lunch && (
                    <div className="mt-3">
                      <details className="cursor-pointer">
                        <summary className="text-blue-400 text-sm">View Work Summary</summary>
                        <div className="mt-2 p-3 bg-gray-700 rounded text-xs space-y-2">
                          <div>
                            <p className="text-gray-400 font-medium mb-1">Before Lunch:</p>
                            <p className="text-gray-300">{record.work_before_lunch}</p>
                          </div>
                          <div>
                            <p className="text-gray-400 font-medium mb-1">After Lunch:</p>
                            <p className="text-gray-300">{record.work_after_lunch}</p>
                          </div>
                        </div>
                      </details>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination */}
            {total > itemsPerPage && (
              <div className="bg-gray-900 px-4 py-3 flex items-center justify-between border-t border-gray-700">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                    disabled={currentPage === 0}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-md text-gray-300 bg-gray-800 hover:bg-gray-700 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={(currentPage + 1) * itemsPerPage >= total}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-md text-gray-300 bg-gray-800 hover:bg-gray-700 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-400">
                      Showing{' '}
                      <span className="font-medium">{currentPage * itemsPerPage + 1}</span> to{' '}
                      <span className="font-medium">
                        {Math.min((currentPage + 1) * itemsPerPage, total)}
                      </span>{' '}
                      of <span className="font-medium">{total}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                        disabled={currentPage === 0}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-600 bg-gray-800 text-sm font-medium text-gray-400 hover:bg-gray-700 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={(currentPage + 1) * itemsPerPage >= total}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-600 bg-gray-800 text-sm font-medium text-gray-400 hover:bg-gray-700 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AttendanceRecords;
